import { and } from 'tio-ui/utilities';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import Component from '@glimmer/component';
import DescriptionList from 'tio-ui/components/description-list';
import Svg from 'tio-employee/components/svg';
import CourseCashLegal from './course-cash-legal';
import { PhClock } from 'ember-phosphor-icons';
import {
  StripeIssuingSession,
  mountIssuing,
} from 'tio-employee/utils/tas/course-cash/stripe-issuing-session';
import type Owner from '@ember/owner';
import type IntlService from 'ember-intl/services/intl';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';

export interface CardDetailsTabSignature {
  Args: {
    application: TasApplicationModel;
  };
  Element: HTMLDivElement;
}

/**
 * "Card Details" tab of the manage-screen CourseCash drawer. Always shows the
 * revealed card number/CVV/expiry as soon as Stripe auth is ready — no
 * "View Card Details" click-to-reveal gate, unlike the legacy show-page's
 * `StripeCardDisplay` (a deliberate product decision: opening this tab IS the
 * reveal action). Shares the same `StripeIssuingSession` Stripe Issuing
 * ephemeral-key plumbing as `stripe-card-display.gts` — see that module's
 * doc comment for why this can't be a plain data field.
 */
export default class CardDetailsTab extends Component<CardDetailsTabSignature> {
  @service declare intl: IntlService;

  private session = new StripeIssuingSession(this.cardId, this.stripeCardProviderId);

  constructor(owner: Owner, args: CardDetailsTabSignature['Args']) {
    super(owner, args);
    const companyAccountProviderId = this.card?.pcsCompany?.providerId || '';
    if (companyAccountProviderId) {
      void this.session.setup(companyAccountProviderId);
    }
  }

  willDestroy(...args: Parameters<Component<CardDetailsTabSignature>['willDestroy']>) {
    super.willDestroy(...args);
    this.session.teardown();
  }

  get card() {
    return (
      this.args.application.pcsCards?.find((card) => card.isActive) ??
      this.args.application.pcsCards?.[0]
    );
  }

  get isCardActive() {
    return this.card?.isActive ?? false;
  }

  get isCardDeactivated() {
    return this.card?.isDeactivated ?? false;
  }

  get isCardActiveOrDeactivated() {
    return this.isCardActive || this.isCardDeactivated;
  }

  get stripeCardProviderId() {
    return this.card?.providerId || '';
  }

  get cardId() {
    return this.card?.id || '';
  }

  get lastFour() {
    return (this.card?.providerData?.last4 as string) ?? '';
  }

  get legalName() {
    return (this.card?.pcsCardHolder?.legalName as string) ?? '';
  }

  get companyName() {
    return (this.card?.pcsCompany?.providerData?.company?.name as string) ?? '';
  }

  get zipCode() {
    return (this.card?.pcsCardHolder?.providerData?.billing?.address?.postal_code as string) ?? '';
  }

  get institutionName() {
    return this.args.application.tasProgramInstance.institutionName;
  }

  // Same source the "Your CourseCa$h™ is available!" task card uses
  // (needs-your-attention.gts's cardBalanceDollars) — reserved minus used, so
  // a partially-spent card shows the true remaining balance, not the
  // original reserved total.
  get cardRemainingAmountCents() {
    return Math.max(0, this.args.application.courseCashAvailableBalance ?? 0);
  }

  get formattedCardRemainingAmount() {
    const dollars = this.cardRemainingAmountCents / 100;
    return this.intl.formatNumber(dollars, {
      style: 'currency',
      currency: 'USD',
    });
  }

  // The live course cash reserve carries the rolling funds-expiry date; captured/
  // consumed siblings return null, so pick the one with a pending deadline.
  get courseCashReserve() {
    return this.args.application.tasAssistances?.find(
      (assistance) => assistance.isCourseCash && assistance.courseCashExpiresAt
    );
  }

  get formattedFundsExpiryDate() {
    const raw = this.courseCashReserve?.courseCashExpiresAt;
    if (!raw) return null;

    // raw is a date-only string ("YYYY-MM-DD"); parse the parts as a local date so a
    // UTC-midnight value is not shifted back a day in the browser's timezone.
    const [year, month, day] = raw.slice(0, 10).split('-').map(Number);
    if (!year || !month || !day) return null;

    return this.intl.formatDate(new Date(year, month - 1, day), {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  <template>
    <div class="tio-col gap-6 p-6" ...attributes>
      {{#if this.isCardActive}}
        <dl class="tio-col gap-1">
          {{#if this.institutionName}}
            <div class="tio-row flex-wrap items-baseline gap-2">
              <dt class="tio-text-sm-medium tio-fg-secondary">
                {{t "tuition_assistance.applications.manage.tasks.authorized_at"}}
              </dt>
              <dd class="tio-text-sm-normal tio-fg-primary">
                {{this.institutionName}}
                <span class="tio-text-sm-normal tio-fg-secondary italic">
                  {{t "tuition_assistance.applications.manage.tasks.authorized_at_hint"}}
                </span>
              </dd>
            </div>
          {{/if}}
          <div class="tio-row flex-wrap items-baseline gap-2">
            <dt class="tio-text-sm-medium tio-fg-secondary">
              {{t "tuition_assistance.applications.manage.tasks.available_balance"}}
            </dt>
            <dd class="tio-text-sm-normal tio-fg-primary">
              {{this.formattedCardRemainingAmount}}
            </dd>
          </div>
          {{#if this.formattedFundsExpiryDate}}
            <div class="tio-row flex-wrap items-baseline gap-2">
              <dt class="tio-text-sm-medium tio-fg-secondary">
                {{t "tuition_assistance.applications.manage.tasks.use_by_label"}}
              </dt>
              <dd class="tio-text-sm-normal tio-fg-primary">
                {{this.formattedFundsExpiryDate}}
                <span class="tio-text-sm-normal tio-fg-secondary italic">
                  {{t "tuition_assistance.applications.manage.tasks.use_by_hint"}}
                </span>
              </dd>
            </div>
          {{/if}}
        </dl>

        {{! Card visual — revealed as soon as Stripe auth is ready, masked with last4 until then }}
        <div
          class="relative mx-auto w-full max-w-xs aspect-[85.6/54] rounded-2xl tio-course-cash-card tio-fg-inverse shadow-xl tio-course-cash-card-frame"
        >
          <div class="absolute inset-0 z-0 pointer-events-none tio-flex-end items-end px-5 pb-4">
            {{! template-lint-disable no-bare-strings }}
            <Svg @name="visa" fill="currentColor" height="40%" role="img" aria-label="VISA" />
            {{! template-lint-enable no-bare-strings }}
          </div>

          <div class="absolute inset-0 px-5 py-4 tio-col justify-between">
            <div>
              <div class="tio-text-xs-medium opacity-70">
                {{t "tuition_assistance.applications.manage.card.balance_label"}}
              </div>
              <div class="tio-text-2xl-semibold mt-1">{{this.formattedCardRemainingAmount}}</div>
            </div>

            {{#if (and this.session.auth this.session.elements)}}
              <div
                class="block min-h-8"
                {{mountIssuing
                  "issuingCardNumberDisplay"
                  "cardNumber"
                  this.session.elements
                  this.stripeCardProviderId
                  this.session.auth
                  this.session.register
                }}
              />

              <div class="flex gap-8">
                <div class="tio-col-extra-tight">
                  <span class="tio-text-overline tio-fg-inverse">{{t
                      "tas.stripe_details.cvv"
                    }}</span>
                  <div class="w-8">
                    <div
                      class="block min-h-6"
                      {{mountIssuing
                        "issuingCardCvcDisplay"
                        "cardDetails"
                        this.session.elements
                        this.stripeCardProviderId
                        this.session.auth
                        this.session.register
                      }}
                    />
                  </div>
                </div>

                <div class="tio-col-extra-tight">
                  <span class="tio-text-overline tio-fg-inverse">{{t
                      "tas.stripe_details.valid_thru"
                    }}</span>
                  <div
                    class="block min-h-6"
                    {{mountIssuing
                      "issuingCardExpiryDisplay"
                      "cardDetails"
                      this.session.elements
                      this.stripeCardProviderId
                      this.session.auth
                      this.session.register
                    }}
                  />
                </div>
              </div>
            {{else}}
              <p class="tracking-widest sm:text-lg font-semibold py-2">
                <span aria-hidden="true">• • • •</span>
                <span class="ml-1">{{this.lastFour}}</span>
                <span class="sr-only">{{t "tas.stripe_details.card_ending_in"}}
                  {{this.lastFour}}</span>
              </p>
            {{/if}}
          </div>
        </div>

        {{! Card Details DescriptionList }}
        <DescriptionList @cols={{2}} @divided={{true}} @compact={{true}} as |List|>
          <List.Group data-test-card-details-cardholder-group>
            <List.Term>{{t "tas.stripe_details.cardholder_name"}}</List.Term>
            <List.Details class="font-semibold tracking-wide">{{this.legalName}}</List.Details>
          </List.Group>

          {{#if this.companyName}}
            <List.Group>
              <List.Term>{{t "tas.stripe_details.company_name"}}</List.Term>
              <List.Details class="font-semibold tracking-wide">{{this.companyName}}</List.Details>
            </List.Group>
          {{/if}}

          <List.Group>
            <List.Term>{{t "tas.stripe_details.card_number"}}</List.Term>
            <List.Details class="font-semibold tracking-wide">
              {{#if (and this.session.auth this.session.elements)}}
                <div
                  class="min-h-6"
                  {{mountIssuing
                    "issuingCardNumberDisplay"
                    "descriptionList"
                    this.session.elements
                    this.stripeCardProviderId
                    this.session.auth
                    this.session.register
                  }}
                />
              {{else}}
                **** **** ****
                {{this.lastFour}}
              {{/if}}
            </List.Details>
          </List.Group>

          <List.Group>
            <List.Term>{{t "tas.stripe_details.cvv"}}</List.Term>
            <List.Details class="font-semibold tracking-wide">
              {{#if (and this.session.auth this.session.elements)}}
                <div
                  class="min-h-6"
                  {{mountIssuing
                    "issuingCardCvcDisplay"
                    "descriptionList"
                    this.session.elements
                    this.stripeCardProviderId
                    this.session.auth
                    this.session.register
                  }}
                />
              {{else}}
                ***
              {{/if}}
            </List.Details>
          </List.Group>

          <List.Group>
            <List.Term>{{t "tas.stripe_details.expiration_date"}}</List.Term>
            <List.Details class="font-semibold tracking-wide">
              {{#if (and this.session.auth this.session.elements)}}
                <div
                  class="min-h-6"
                  {{mountIssuing
                    "issuingCardExpiryDisplay"
                    "descriptionList"
                    this.session.elements
                    this.stripeCardProviderId
                    this.session.auth
                    this.session.register
                  }}
                />
              {{else}}
                **/**/****
              {{/if}}
            </List.Details>
          </List.Group>

          {{#if this.zipCode}}
            <List.Group>
              <List.Term>{{t "tas.stripe_details.zip_code"}}</List.Term>
              <List.Details class="font-semibold tracking-wide">{{this.zipCode}}</List.Details>
            </List.Group>
          {{/if}}
        </DescriptionList>

      {{else if this.isCardDeactivated}}
        <div class="tio-place-center py-4 px-4 text-center" data-test-deactivated-card-alert>
          <p class="tio-text-sm-normal tio-fg-secondary">
            {{t "tas.stripe_details.card_deactivated_message"}}
          </p>
        </div>
      {{else}}
        <div class="tio-place-center py-4 px-4 text-center">
          <div class="p-6 w-full">
            <div class="mb-3">
              <PhClock @size={{48}} class="mx-auto tio-fg-brand" />
            </div>
            <h3 class="tio-text-lg-semibold tio-fg-primary mb-2">
              {{t "tas.stripe_details.card_activation_pending"}}
            </h3>
            <p class="text-sm tio-fg-secondary">
              {{t "tas.stripe_details.card_activation_message"}}
            </p>
          </div>
        </div>
      {{/if}}

      {{! Guidelines stay gated on an active card, as before the extraction —
          a deactivated card has nothing left to spend. The issuer disclosure
          renders either way. }}
      <CourseCashLegal @context="card-details" @showGuidelines={{this.isCardActive}} />
    </div>
  </template>
}
